import React, { useState, useEffect } from 'react';
import { InformePericial, FaseInforme } from '../../types';
import { informeService } from '../../services/api';
import AudioRecorder from '../AudioRecorder';
import { FiLoader } from 'react-icons/fi';
import { getAbsoluteUrl } from '../../utils/url';
import './EncargoForm.css';

interface GrabacionPeritoFaseProps {
    informe?: InformePericial;
    onUpdate: (informe: InformePericial, targetFase?: FaseInforme) => void;
}

const GrabacionPeritoFase: React.FC<GrabacionPeritoFaseProps> = ({ informe, onUpdate }) => {
    const [loading, setLoading] = useState(false);
    const [savingText, setSavingText] = useState(false);
    const [grabado, setGrabado] = useState(false);
    const [audioUrl, setAudioUrl] = useState<string | null>(null);
    const [transcripcion, setTranscripcion] = useState<string | null>(null);
    const [transcripcionEditada, setTranscripcionEditada] = useState<string | null>(null);
    const [error, setError] = useState('');

    useEffect(() => {
        if (informe) {
            prvCargarEstado();
        }
    }, [informe]);

    const prvCargarEstado = async () => {
        if (!informe) return;
        try {
            const res = await informeService.obtenerGrabacionPerito(informe.id);
            setGrabado(res.data.grabado);
            setAudioUrl(res.data.audio_url);
            setTranscripcion(res.data.transcripcion);
            setTranscripcionEditada(res.data.transcripcion);
        } catch (err: any) {
            console.error('Error cargando estado de grabación perito:', err);
        }
    };

    const prvHandleSiguienteFase = async () => {
        if (!informe) return;
        let updatedInforme = informe;
        try {
            const res = await informeService.cambiarFase(informe.id, 'previsualizacionContexto');
            updatedInforme = res.data;
        } catch (e) {
            console.error('Error al cambiar de fase:', e);
        }
        onUpdate(updatedInforme, 'previsualizacionContexto');
    };

    const prvHandleRecordingComplete = async (file: File | null) => {
        if (!informe) return;
        if (!file) {
            setLoading(true);
            setError('');
            try {
                await informeService.eliminarGrabacionPerito(informe.id);
                setGrabado(false);
                setAudioUrl(null);
                setTranscripcion(null);
                setTranscripcionEditada(null);
            } catch (err: any) {
                console.error('Error al eliminar la grabación:', err);
                setError(err.response?.data?.error || 'Error al eliminar la grabación');
            } finally {
                setLoading(false);
            }
            return;
        }

        setLoading(true);
        setError('');

        try {
            await informeService.subirGrabacionPerito(informe.id, file);
            await prvCargarEstado();
        } catch (err: any) {
            console.error('Error al subir grabación perito:', err);
            setError(err.response?.data?.error || 'Error al subir la grabación del perito');
        } finally {
            setLoading(false);
        }
    };

    const prvHandleSaveTranscripcion = async () => {
        if (!informe || transcripcionEditada === null) return;
        setSavingText(true);
        setError('');
        try {
            await informeService.actualizarGrabacionTranscripcion(informe.id, transcripcionEditada);
            setTranscripcion(transcripcionEditada);
        } catch (err: any) {
            console.error('Error al guardar transcripción:', err);
            setError(err.response?.data?.error || 'Error al guardar la transcripción');
        } finally {
            setSavingText(false);
        }
    };

    return (
        <div className="encargo-form">
            <div className="form-header-section flex justify-between items-center mb-4">
                <div>
                    <h3 className="section-title mb-1">Grabación de Observaciones del Perito</h3>
                </div>
                {informe && (
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={prvHandleSiguienteFase}
                    >
                        Siguiente Fase
                    </button>
                )}
            </div>

            {error && <div className="error-message mb-4">{error}</div>}

            <div className="form-section mb-6">
                <AudioRecorder
                    onRecordingComplete={prvHandleRecordingComplete}
                    existingAudioUrl={audioUrl ? getAbsoluteUrl(audioUrl) : null}
                />
            </div>

            {loading && (
                <div className="loading-state text-center py-6">
                    <FiLoader className="animate-spin text-blue-600 text-3xl mx-auto mb-2" />
                    <p className="text-sm text-gray-600 font-medium">Procesando y transcribiendo audio... Por favor espera.</p>
                </div>
            )}

            {grabado && transcripcionEditada !== null && !loading && (
                <div className="form-section mb-6">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <label className="font-medium text-gray-700" style={{ margin: 0 }}>Transcripción de Observaciones (Editable)</label>
                        {transcripcionEditada !== transcripcion && (
                            <button
                                type="button"
                                onClick={prvHandleSaveTranscripcion}
                                disabled={savingText}
                                style={{
                                    fontSize: '12px',
                                    padding: '4px 10px',
                                    backgroundColor: '#2563eb',
                                    color: '#ffffff',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer'
                                }}
                            >
                                {savingText ? 'Guardando...' : 'Guardar Transcripción'}
                            </button>
                        )}
                    </div>
                    <textarea
                        value={transcripcionEditada}
                        onChange={(e) => setTranscripcionEditada(e.target.value)}
                        style={{
                            width: '100%',
                            height: '160px',
                            padding: '12px',
                            border: '1px solid #cbd5e1',
                            borderRadius: '8px',
                            fontSize: '14px',
                            fontFamily: 'monospace',
                            resize: 'vertical',
                            boxSizing: 'border-box'
                        }}
                        placeholder="Edita la transcripción obtenida si es necesario..."
                    />
                </div>
            )}
        </div>
    );
};

export default GrabacionPeritoFase;
